// lib/dateFormat.js
// App-wide date/time formatters. Single source of truth so every screen
// prints dates identically. Mirrors backend `services/date_format.py`.
//
// PUBLIC API
//   formatDate("2026-07-25")           → "07/25/2026"
//   formatDate(new Date())             → "07/25/2026"
//   formatDateTime("2026-07-25T17:35") → "07/25/2026 5:35 PM"
//   formatRelative("2026-07-25T14:00") → "2h ago"   (unchanged UX for chat/notifs)
//   toISO(new Date())                  → "2026-07-25"
//
// EDGE CASES
//   • Any bad / null / empty input returns "" so callers can safely do
//     `formatDate(row.dob) || "—"`.
//   • Times are always local, using the browser's timezone.

// ── Coerce to a valid Date, or null ────────────────────────────────────────
function _asDate(input) {
  if (input == null || input === "") return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  // Bare "YYYY-MM-DD" strings should be parsed as LOCAL, not UTC (avoids
  // "07/24/2026" showing as "07/25/2026" for users east of UTC).
  if (typeof input === "string" && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    const [y, m, d] = input.split("-").map(Number);
    const dt = new Date(y, m - 1, d);
    return isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(input);
  return isNaN(dt.getTime()) ? null : dt;
}

const _pad2 = (n) => String(n).padStart(2, "0");

// ── Public ────────────────────────────────────────────────────────────────
export function formatDate(input) {
  const d = _asDate(input);
  if (!d) return "";
  return `${_pad2(d.getMonth() + 1)}/${_pad2(d.getDate())}/${d.getFullYear()}`;
}

export function formatDateTime(input) {
  const d = _asDate(input);
  if (!d) return "";
  let h = d.getHours();
  const m = _pad2(d.getMinutes());
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${formatDate(d)} ${h}:${m} ${ampm}`;
}

// Replace any bare ISO date (YYYY-MM-DD) inside a free-text string with the
// app-standard MM/DD/YYYY. Used for human-readable notes that embed a date.
export function humanizeDates(input) {
  if (!input || typeof input !== "string") return input || "";
  return input.replace(/\b(\d{4})-(\d{2})-(\d{2})\b/g, (_, y, m, d) => `${m}/${d}/${y}`);
}

export function toISO(input) {
  const d = _asDate(input);
  if (!d) return "";
  return `${d.getFullYear()}-${_pad2(d.getMonth() + 1)}-${_pad2(d.getDate())}`;
}

// Relative timestamps kept intact — deliberately preserved for chat + notifications.
export function formatRelative(input) {
  const d = _asDate(input);
  if (!d) return "";
  const diffMs = Date.now() - d.getTime();
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return "just now";
  const min = Math.floor(s / 60);
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(d);
}
